// src/pages/WarImpact.tsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { warImpact } from '@/data/mock/warImpact';
import MartyrsTeam from './MartyrsTeam';
import MartyrsPlayers from './MartyrsPlayers';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart,
  TrendingUp,
  AlertTriangle,
  Building2,
  Users,
  Trophy,
  Shield,
  Flame,
  Target,
  Calendar,
  MapPin,
  Minus,
  Plus,
  Info,
  Lightbulb,
  Zap,
  Award,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingDown,
  Activity,
  AlertCircle,
  Sunrise,
  HandHeart,
  Sparkles,
  Flag,
  BookOpen,
  MessageCircle
} from 'lucide-react';

const WarImpact = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  const [showFullTimeline, setShowFullTimeline] = useState(false);

  // Tabbed sections: war impact (default), team martyrs, player martyrs.
  // Driven by URL hash so /war-impact#team and /war-impact#players deep-link in.
  const hashToTab = (hash: string): 'impact' | 'team' | 'players' => {
    if (hash === '#team') return 'team';
    if (hash === '#players') return 'players';
    return 'impact';
  };
  const [activeTab, setActiveTab] = useState<'impact' | 'team' | 'players'>(hashToTab(location.hash));

  useEffect(() => {
    setActiveTab(hashToTab(location.hash));
  }, [location.hash]);

  const tabs = [
    { id: 'impact' as const, icon: Activity, label: { en: 'War Impact', ar: 'تأثير الحرب' }, hash: '' },
    { id: 'team' as const, icon: Award, label: { en: 'Team Martyrs', ar: 'شهداء الفريق' }, hash: '#team' },
    { id: 'players' as const, icon: Trophy, label: { en: 'Player Martyrs', ar: 'شهداء اللاعبين' }, hash: '#players' },
  ];

  const selectTab = (tab: typeof tabs[number]) => {
    setActiveTab(tab.id);
    window.history.replaceState(null, '', `${location.pathname}${tab.hash}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Timeline Events (أحداث رئيسية)
  const timelineEvents = [
    {
      date: '2023-10-07',
      title: { en: 'War Begins', ar: 'بداية الحرب' },
      description: {
        en: 'Conflict escalates, forcing immediate closure of all academy facilities for safety.',
        ar: 'تصاعد الصراع، مما أجبر على الإغلاق الفوري لجميع مرافق الأكاديمية للسلامة.'
      },
      icon: AlertTriangle,
      color: 'text-red-500'
    },
    {
      date: '2023-10-15',
      title: { en: 'Emergency Response', ar: 'استجابة طارئة' },
      description: {
        en: 'Academy staff began providing emergency aid and psychological support to affected families.',
        ar: 'بدأ طاقم الأكاديمية في تقديم المساعدات الطارئة والدعم النفسي للعائلات المتضررة.'
      },
      icon: HandHeart,
      color: 'text-blue-500'
    },
    {
      date: '2023-11-01',
      title: { en: 'Facilities Damaged', ar: 'تضرر المرافق' },
      description: {
        en: 'Main training center sustained significant damage. Equipment and resources lost.',
        ar: 'تعرض مركز التدريب الرئيسي لأضرار كبيرة. فقدان المعدات والموارد.'
      },
      icon: Building2,
      color: 'text-orange-500'
    },
    {
      date: '2023-12-01',
      title: { en: 'Adapted Programs', ar: 'برامج مُعدّلة' },
      description: {
        en: 'Started modified training programs in safe zones, focusing on mental health and hope.',
        ar: 'بدأت برامج تدريبية معدلة في مناطق آمنة، مع التركيز على الصحة النفسية والأمل.'
      },
      icon: Lightbulb,
      color: 'text-green-500'
    },
    {
      date: '2024-01-01',
      title: { en: 'Resilience & Hope', ar: 'صمود وأمل' },
      description: {
        en: 'Despite ongoing challenges, academy continues serving children with adapted programs.',
        ar: 'رغم التحديات المستمرة، تواصل الأكاديمية خدمة الأطفال ببرامج معدلة.'
      },
      icon: Sunrise,
      color: 'text-primary'
    },
  ];

  // Impact Categories
  const impactCategories = [
    {
      category: { en: 'Facilities', ar: 'المرافق' },
      icon: Building2,
      impacts: [
        {
          name: { en: 'Training Centers', ar: 'مراكز التدريب' },
          before: '7',
          after: '3',
          status: 'critical',
          description: { en: 'Operational centers', ar: 'مراكز تشغيلية' }
        },
        {
          name: { en: 'Sports Fields', ar: 'ملاعب رياضية' },
          before: '12',
          after: '5',
          status: 'critical',
          description: { en: 'Usable fields', ar: 'ملاعب قابلة للاستخدام' }
        },
        {
          name: { en: 'Equipment Sets', ar: 'مجموعات المعدات' },
          before: '500+',
          after: '150',
          status: 'critical',
          description: { en: 'Available equipment', ar: 'معدات متاحة' }
        },
      ]
    },
    {
      category: { en: 'Programs', ar: 'البرامج' },
      icon: Trophy,
      impacts: [
        {
          name: { en: 'Active Athletes', ar: 'رياضيون نشطون' },
          before: '2000+',
          after: '600',
          status: 'warning',
          description: { en: 'Currently training', ar: 'يتدربون حالياً' }
        },
        {
          name: { en: 'Training Sessions/Week', ar: 'جلسات/أسبوع' },
          before: '150',
          after: '45',
          status: 'warning',
          description: { en: 'Weekly sessions', ar: 'جلسات أسبوعية' }
        },
        {
          name: { en: 'Sports Offered', ar: 'رياضات مقدمة' },
          before: '8',
          after: '4',
          status: 'warning',
          description: { en: 'Available sports', ar: 'رياضات متاحة' }
        },
      ]
    },
    {
      category: { en: 'Human Impact', ar: 'الأثر البشري' },
      icon: Users,
      impacts: [
        {
          name: { en: 'Staff Members', ar: 'أعضاء الطاقم' },
          before: '85',
          after: '42',
          status: 'critical',
          description: { en: 'Active staff', ar: 'طاقم نشط' }
        },
        {
          name: { en: 'Coaches', ar: 'مدربون' },
          before: '45',
          after: '20',
          status: 'critical',
          description: { en: 'Available coaches', ar: 'مدربون متاحون' }
        },
        {
          name: { en: 'Families Affected', ar: 'عائلات متأثرة' },
          before: '0',
          after: '500+',
          status: 'critical',
          description: { en: 'Families in need', ar: 'عائلات محتاجة' }
        },
      ]
    },
  ];

  // Memorial Section (in memory)
  const memorialStats = {
    martyrs: { value: '15', label: { en: 'Staff & Athletes Lost', ar: 'من الطاقم واللاعبين' } },
    displaced: { value: '1000+', label: { en: 'Families Displaced', ar: 'عائلة نازحة' } },
    injured: { value: '50+', label: { en: 'Injured', ar: 'مصاب' } },
  };

  // Resilience & Adaptation
  const resilienceActions = [
    {
      icon: Shield,
      title: { en: 'Safety First', ar: 'السلامة أولاً' },
      description: {
        en: 'Relocated programs to safer zones, prioritizing children\'s safety above all.',
        ar: 'نقل البرامج إلى مناطق أكثر أماناً، مع إعطاء الأولوية لسلامة الأطفال.'
      }
    },
    {
      icon: Heart,
      title: { en: 'Mental Health Support', ar: 'دعم الصحة النفسية' },
      description: {
        en: 'Integrated psychological support into all programs to help children cope with trauma.',
        ar: 'دمج الدعم النفسي في جميع البرامج لمساعدة الأطفال على التعامل مع الصدمة.'
      }
    },
    {
      icon: Users,
      title: { en: 'Community Unity', ar: 'وحدة المجتمع' },
      description: {
        en: 'Strengthened community bonds through collective sports activities and mutual support.',
        ar: 'تقوية الروابط المجتمعية من خلال الأنشطة الرياضية الجماعية والدعم المتبادل.'
      }
    },
    {
      icon: Lightbulb,
      title: { en: 'Adapted Curriculum', ar: 'منهج معدل' },
      description: {
        en: 'Modified training programs to work with limited resources while maintaining quality.',
        ar: 'تعديل برامج التدريب للعمل بموارد محدودة مع الحفاظ على الجودة.'
      }
    },
    {
      icon: HandHeart,
      title: { en: 'Emergency Aid', ar: 'مساعدات طارئة' },
      description: {
        en: 'Distributed food, water, and essential supplies to families in need.',
        ar: 'توزيع الطعام والماء والإمدادات الأساسية للعائلات المحتاجة.'
      }
    },
    {
      icon: Sparkles,
      title: { en: 'Hope Programs', ar: 'برامج الأمل' },
      description: {
        en: 'Created special programs focused on resilience, hope, and future dreams.',
        ar: 'إنشاء برامج خاصة تركز على الصمود والأمل والأحلام المستقبلية.'
      }
    },
  ];

  // Testimonials
  const testimonials = [
    {
      name: { en: 'Coach Ahmed', ar: 'المدرب أحمد' },
      role: { en: 'Football Coach', ar: 'مدرب كرة قدم' },
      quote: {
        en: 'Despite everything, seeing the children smile when they play gives us the strength to continue. Sports is their escape, their hope.',
        ar: 'رغم كل شيء، رؤية ابتسامة الأطفال عندما يلعبون تعطينا القوة للاستمرار. الرياضة هي هروبهم وأملهم.'
      }
    },
    {
      name: { en: 'Parent Fatima', ar: 'والدة فاطمة' },
      role: { en: 'Mother of 3 Athletes', ar: 'أم لـ 3 رياضيين' },
      quote: {
        en: 'Champions Academy gave my children something to look forward to during the darkest days. They found normalcy and joy through sports.',
        ar: 'أعطت أكاديمية تشامبيونز أطفالي شيئاً يتطلعون إليه خلال الأيام المظلمة. وجدوا الطبيعية والفرح من خلال الرياضة.'
      }
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('warImpact.title')}
        subtitle={isRTL ? 'صمود، أمل، واستمرار في خدمة أطفالنا رغم كل التحديات' : 'Resilience, Hope, and Continued Service to Our Children Despite All Challenges'}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.warImpact') }
        ]}
      />

      {/* Alert Banner */}
      <section className="py-6 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 border-y border-orange-500/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 text-center"
          >
            <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? 'هذه الصفحة توثق أثر الحرب على أكاديمية تشامبيونز وصمودنا المستمر في خدمة مجتمعنا'
                : 'This page documents the war\'s impact on Champions Academy and our continued resilience in serving our community'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section Tabs (War Impact / Team Martyrs / Player Martyrs) */}
      <section className="sticky top-16 md:top-20 z-40 bg-background/95 backdrop-blur border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-2 py-4">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => selectTab(tab)}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 border-2 ${
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  <TabIcon className="h-4 w-4" />
                  {tab.label[isRTL ? 'ar' : 'en']}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {activeTab === 'impact' && (
      <>
      {/* Overview */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Info className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'نظرة عامة' : 'Overview'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'واقع الحرب وأثرها' : 'The Reality of War and Its Impact'}
              </h2>
            </div>

            {/* Content */}
            <div className="p-8 rounded-3xl bg-card border border-border shadow-xl">
              <p className="text-lg text-muted-foreground leading-relaxed text-center">
                {warImpact.overview[isRTL ? 'ar' : 'en']}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Memorial Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Somber Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-surface-1 to-background opacity-50" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500/10 border border-red-500/20 mb-8">
                <Heart className="h-5 w-5 text-red-500" />
                <span className="text-sm font-semibold text-red-500">
                  {isRTL ? 'في الذاكرة' : 'In Memory'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                {isRTL ? 'نتذكر ونكرم' : 'We Remember and Honor'}
              </h2>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {isRTL
                  ? 'نتذكر جميع من فقدناهم ونكرم ذكراهم. سيبقى إرثهم حياً في قلوبنا وفي كل طفل نخدمه.'
                  : 'We remember all those we lost and honor their memory. Their legacy lives on in our hearts and in every child we serve.'
                }
              </p>
            </div>

            {/* Memorial Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0 }}
                className="p-8 rounded-3xl bg-card border-2 border-red-500/30 shadow-xl text-center"
              >
                <div className="text-6xl font-bold text-red-500 mb-3">{memorialStats.martyrs.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {memorialStats.martyrs.label[isRTL ? 'ar' : 'en']}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <Heart className="h-6 w-6 text-red-500 mx-auto" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-3xl bg-card border border-border shadow-xl text-center"
              >
                <div className="text-6xl font-bold text-orange-500 mb-3">{memorialStats.displaced.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {memorialStats.displaced.label[isRTL ? 'ar' : 'en']}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <Users className="h-6 w-6 text-orange-500 mx-auto" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-8 rounded-3xl bg-card border border-border shadow-xl text-center"
              >
                <div className="text-6xl font-bold text-yellow-500 mb-3">{memorialStats.injured.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {memorialStats.injured.label[isRTL ? 'ar' : 'en']}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <AlertTriangle className="h-6 w-6 text-yellow-500 mx-auto" />
                </div>
              </motion.div>
            </div>

            {/* Memorial Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-3xl bg-red-500/5 border border-red-500/20 text-center"
            >
              <p className="text-lg text-foreground leading-relaxed italic">
                {isRTL
                  ? '"سنواصل العمل بروح من فقدناهم، ونحمل رسالتهم في قلوبنا، ونعد بأن نكون على قدر التضحيات التي قدموها."'
                  : '"We will continue working with the spirit of those we lost, carrying their message in our hearts, and we promise to live up to the sacrifices they made."'
                }
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Flag className="h-4 w-4" />
                <span className="font-semibold">{isRTL ? 'أكاديمية تشامبيونز' : 'Champions Academy'}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Clock className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'الجدول الزمني' : 'Timeline'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'رحلة الصمود' : 'Journey of Resilience'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'أبرز الأحداث والمراحل التي مررنا بها'
                : 'Key events and stages we went through'
              }
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="max-w-4xl mx-auto relative">
            {/* Timeline Line */}
            <div className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} top-0 bottom-0 w-1 bg-gradient-to-b from-red-500 via-orange-500 to-green-500`} />

            {/* Events */}
            {timelineEvents.map((event, idx) => {
              const Icon = event.icon;
              const isEven = idx % 2 === 0;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`relative flex items-center mb-16 ${
                    isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${
                    isRTL 
                      ? (isEven ? 'md:text-left md:pl-20' : 'md:pr-20')
                      : (isEven ? 'md:text-right md:pr-20' : 'md:pl-20')
                  }`}>
                    <div className="md:inline-block w-full md:w-auto bg-card border border-border rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-xl bg-${event.color}/10`}>
                          <Icon className={`h-6 w-6 ${event.color}`} />
                        </div>
                        <span className="text-sm text-muted-foreground font-medium">
                          {new Date(event.date).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {event.title[isRTL ? 'ar' : 'en']}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {event.description[isRTL ? 'ar' : 'en']}
                      </p>
                    </div>
                  </div>

                  {/* Node */}
                  <div className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} w-6 h-6 rounded-full border-4 border-background shadow-lg ${event.color.replace('text-', 'bg-')} transform -translate-x-1/2`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before & After Comparison */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Activity className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'المقارنة' : 'Comparison'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'قبل وبعد الحرب' : 'Before and After the War'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-12">
              {isRTL
                ? 'مقارنة شاملة توضح التأثير على جميع جوانب الأكاديمية'
                : 'A comprehensive comparison showing the impact on all aspects of the academy'
              }
            </p>

            {/* Legend */}
            <div className="flex justify-center gap-8 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500" />
                <span className="text-sm font-medium text-muted-foreground">
                  {isRTL ? 'قبل الحرب' : 'Before War'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500" />
                <span className="text-sm font-medium text-muted-foreground">
                  {isRTL ? 'أثناء الحرب' : 'During War'}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Impact Categories */}
          <div className="max-w-6xl mx-auto space-y-12">
            {impactCategories.map((category, catIdx) => {
              const CategoryIcon = category.icon;

              return (
                <motion.div
                  key={catIdx}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: catIdx * 0.1 }}
                  className="p-8 rounded-3xl bg-card border border-border shadow-xl"
                >
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
                      <CategoryIcon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-3xl font-bold">
                      {category.category[isRTL ? 'ar' : 'en']}
                    </h3>
                  </div>

                  {/* Impacts */}
                  <div className="space-y-6">
                    {category.impacts.map((impact, impIdx) => {
                      const beforeNum = parseInt(impact.before.replace('+', ''));
                      const afterNum = parseInt(impact.after.replace('+', ''));
                      const changePercent = beforeNum > 0 
                        ? Math.round(((afterNum - beforeNum) / beforeNum) * 100)
                        : 0;

                      return (
                        <div
                          key={impIdx}
                          className="p-6 rounded-2xl bg-surface-1 border border-border"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold">
                              {impact.name[isRTL ? 'ar' : 'en']}
                            </h4>
                            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                              impact.status === 'critical' 
                                ? 'bg-red-500/10 text-red-500'
                                : 'bg-orange-500/10 text-orange-500'
                            }`}>
                              {changePercent}%
                              <TrendingDown className="inline h-3 w-3 ml-1" />
                            </span>
                          </div>

                          {/* Comparison Bar */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">
                                  {isRTL ? 'قبل' : 'Before'}
                                </span>
                                <span className="text-2xl font-bold text-green-500">
                                  {impact.before}
                                </span>
                              </div>
                              <div className="h-3 rounded-full bg-green-500/20">
                                <div className="h-full rounded-full bg-green-500" style={{ width: '100%' }} />
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">
                                  {isRTL ? 'الآن' : 'Now'}
                                </span>
                                <span className="text-2xl font-bold text-red-500">
                                  {impact.after}
                                </span>
                              </div>
                              <div className="h-3 rounded-full bg-red-500/20">
                                <div 
                                  className="h-full rounded-full bg-red-500" 
                                  style={{ width: `${(afterNum / beforeNum) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>

                          <p className="text-xs text-muted-foreground mt-3">
                            {impact.description[isRTL ? 'ar' : 'en']}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resilience & Adaptation */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'الصمود' : 'Resilience'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'كيف تكيفنا واستمرينا' : 'How We Adapted and Continued'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'الإجراءات والتكيفات التي اتخذناها لمواصلة خدمة مجتمعنا'
                : 'The actions and adaptations we took to continue serving our community'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {resilienceActions.map((action, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-6">
                  <action.icon className="h-8 w-8 text-primary" />
                </div>

                <h3 className="text-xl font-bold mb-3">
                  {action.title[isRTL ? 'ar' : 'en']}
                </h3>

                <p className="text-muted-foreground leading-relaxed">
                  {action.description[isRTL ? 'ar' : 'en']}
                </p>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-green-500">
                      {isRTL ? 'تم التنفيذ' : 'Implemented'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <MessageCircle className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'شهادات' : 'Testimonials'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'أصوات من المجتمع' : 'Voices from the Community'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-3xl bg-card border border-border shadow-xl relative"
              >
                <BookOpen className="h-12 w-12 text-primary/20 absolute top-6 right-6" />
                
                <div className="relative">
                  <p className="text-lg text-foreground leading-relaxed mb-6 italic">
                    "{testimonial.quote[isRTL ? 'ar' : 'en']}"
                  </p>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-bold">{testimonial.name[isRTL ? 'ar' : 'en']}</div>
                      <div className="text-sm text-muted-foreground">
                        {testimonial.role[isRTL ? 'ar' : 'en']}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hope Message */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/30 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 0.8 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent p-1 mb-8 shadow-2xl"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Sunrise className="h-12 w-12 text-primary" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'الأمل يبقى حياً' : 'Hope Remains Alive'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {isRTL
                ? 'رغم كل التحديات والخسائر، نواصل رسالتنا. كل طفل يبتسم، كل مباراة تُلعب، كل حلم يُحقق - هو انتصار للأمل على اليأس.'
                : 'Despite all challenges and losses, we continue our mission. Every child who smiles, every game played, every dream achieved - is a victory of hope over despair.'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/donate">
                <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                  <HandHeart className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'ساعدنا في الاستمرار' : 'Help Us Continue'}
                </Button>
              </Link>

              <Link to="/humanitarian">
                <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                  <Heart className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'برامجنا الإنسانية' : 'Our Humanitarian Work'}
                </Button>
              </Link>
            </div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-16 pt-16 border-t border-border"
            >
              <p className="text-2xl font-bold gradient-text mb-4">
                {isRTL
                  ? '"في أحلك الأوقات، تكون الرياضة نوراً يضيء الطريق"'
                  : '"In the darkest times, sports is a light that illuminates the path"'
                }
              </p>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">{isRTL ? 'أكاديمية تشامبيونز' : 'Champions Academy'}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </>
      )}

      {activeTab === 'team' && <MartyrsTeam embedded />}
      {activeTab === 'players' && <MartyrsPlayers embedded />}
    </div>
  );
};

export default WarImpact;
